/**
 * Atomic permission catalog — typed mirror of the backend source of truth.
 *
 * Derived verbatim from `scripts/migrations/permissions-catalog.json` (the seed
 * that defines the DB `Resource` table for CASL). The backend remains the
 * runtime authority via GET /permissions/me/permissions — this file only gives
 * the frontend a typed vocabulary so `can()` calls are autocompleted and
 * checked at compile time.
 *
 * ⚠️ Keep in sync with the JSON seed. If a permission is added/removed there,
 * mirror it here (the backend seed has 148, this mirror 122; a test guards each count).
 *
 * The original 102 strings matched the PRD Feature 0 matrix exactly; later
 * additions (e.g. `agent.qa.*` for QA Assistant module RBAC) extend it.
 */

/** Every atomic permission string, grouped by section for ergonomic access. */
export const PERMISSIONS = {
  platform: {
    accounts: {
      create: 'platform.accounts.create',
      viewAssigned: 'platform.accounts.view_assigned',
      switch: 'platform.accounts.switch',
    },
    implementers: {
      view: 'platform.implementers.view',
      assignAccount: 'platform.implementers.assign_account',
      revokeAccount: 'platform.implementers.revoke_account',
    },
  },
  account: {
    information: {
      view: 'account.information.view',
      edit: 'account.information.edit',
    },
    billing: {
      view: 'account.billing.view',
      edit: 'account.billing.edit',
    },
    invoices: {
      view: 'account.invoices.view',
      download: 'account.invoices.download',
    },
  },
  organization: {
    users: {
      view: 'organization.users.view',
      create: 'organization.users.create',
      edit: 'organization.users.edit',
      import: 'organization.users.import',
      deactivate: 'organization.users.deactivate',
      reactivate: 'organization.users.reactivate',
      delete: 'organization.users.delete',
      accessView: 'organization.users.access.view',
      accessManage: 'organization.users.access.manage',
    },
    teams: {
      view: 'organization.teams.view',
      /** Self-scoped: "which teams am I in?". Held by every role, operator included. */
      viewOwn: 'organization.teams.view_own',
      create: 'organization.teams.create',
      edit: 'organization.teams.edit',
      delete: 'organization.teams.delete',
      manageMembers: 'organization.teams.manage_members',
      accessView: 'organization.teams.access.view',
      accessManage: 'organization.teams.access.manage',
    },
    roles: {
      view: 'organization.roles.view',
      create: 'organization.roles.create',
      edit: 'organization.roles.edit',
      delete: 'organization.roles.delete',
      clone: 'organization.roles.clone',
      assign: 'organization.roles.assign',
    },
  },
  folder: {
    view: 'folder.view',
    create: 'folder.create',
    rename: 'folder.rename',
    move: 'folder.move',
    delete: 'folder.delete',
    shareAccess: 'folder.share_access',
  },
  agent: {
    view: 'agent.view',
    create: 'agent.create',
    rename: 'agent.rename',
    move: 'agent.move',
    delete: 'agent.delete',
    shareAccess: 'agent.share_access',
    connectTables: 'agent.connect_tables',
    connectQa: 'agent.connect_qa',
    inbox: {
      view: 'agent.inbox.view',
      takeControl: 'agent.inbox.take_control',
      sendReply: 'agent.inbox.send_reply',
      resolveTicket: 'agent.inbox.resolve_ticket',
      reassign: 'agent.inbox.reassign',
      forceEscalate: 'agent.inbox.force_escalate',
    },
    qa: {
      overview: { view: 'agent.qa.overview.view' },
      upload: { view: 'agent.qa.upload.view' },
      inbox: { view: 'agent.qa.inbox.view' },
      sampling: { view: 'agent.qa.sampling.view' },
      /**
       * Evaluations — Power Skills, Scorecards y Key Moments en una pantalla con
       * pestañas. Reemplaza a las dos entradas sueltas de abajo como sección del
       * Overview, pero `scorecards` y `keyMoments` NO se borran: siguen siendo
       * los permisos que gobiernan cada pestaña por dentro.
       *
       * OJO: este string es nuevo y todavía no está sembrado en los roles del
       * backend. Hasta que lo esté, `can('agent.qa.evaluations.view')` da falso
       * para todo el mundo y la sección no se ve.
       */
      evaluations: { view: 'agent.qa.evaluations.view' },
      scorecards: { view: 'agent.qa.scorecards.view' },
      keyMoments: { view: 'agent.qa.key_moments.view' },
      analytics: { view: 'agent.qa.analytics.view' },
      /**
       * Data Views — las filas crudas del agente, en la tabla dinámica que mantiene.
       *
       * OJO, igual que `evaluations`, `training` y `settings`: este string es nuevo y
       * **todavía no está sembrado en los roles del backend**. Hasta que lo esté,
       * `can('agent.qa.data_views.view')` da falso para todo el mundo y la card no se ve.
       * La ruta queda montada detrás de su `ProtectedRoute`.
       *
       * El PRD v2 se lo da a Admin, QA Manager y Supervisor (este último acotado a su
       * equipo, que el backend ya resuelve por scope de la query).
       */
      dataViews: { view: 'agent.qa.data_views.view' },
      /**
       * Training — hablar con el agente de voz conectado para oír cómo responde.
       *
       * OJO, igual que `evaluations` y `settings`: este string es nuevo y **todavía
       * no está sembrado en los roles del backend**. Hasta que lo esté,
       * `can('agent.qa.training.view')` da falso para todo el mundo y la card no se
       * ve ni con un agente de voz conectado. La ruta queda montada detrás de su
       * `ProtectedRoute`.
       */
      training: { view: 'agent.qa.training.view' },
      /**
       * Knowledge Base — documentos y políticas propios del QA Assistant que
       * respaldan las preguntas de la boleta (QA-Assistant CX-660). Distinto
       * de `kb` más abajo, que es el Knowledge Hub de la plataforma para
       * chatbots — dos conceptos separados, no renombrar ni fusionar.
       *
       * OJO, igual que `evaluations`/`training`/`settings`/`dataViews`: este
       * string es nuevo y todavía no está sembrado en los roles del backend.
       * Hasta que lo esté, `can('agent.qa.knowledge_base.view')` da falso
       * para todo el mundo y ni la tarjeta ni la sección se ven — la ruta
       * queda montada, pero detrás de su `ProtectedRoute`.
       */
      knowledgeBase: { view: 'agent.qa.knowledge_base.view' },
      /**
       * Settings — composición del QA Score, umbrales de aprobación, tramos de
       * score, retención de audio y el auto-cierre por confianza.
       *
       * OJO, igual que `evaluations`: este string es nuevo y todavía no está
       * sembrado en los roles del backend. Hasta que lo esté,
       * `can('agent.qa.settings.view')` da falso para todo el mundo y ni la
       * tarjeta ni la sección se ven — la ruta queda montada, pero detrás de su
       * `ProtectedRoute`.
       */
      settings: { view: 'agent.qa.settings.view' },
    },
    kb: {
      view: 'agent.kb.view',
      uploadSource: 'agent.kb.upload_source',
      syncWebsite: 'agent.kb.sync_website',
      editSource: 'agent.kb.edit_source',
      deleteSource: 'agent.kb.delete_source',
    },
    processFlows: {
      view: 'agent.process_flows.view',
      create: 'agent.process_flows.create',
      edit: 'agent.process_flows.edit',
      duplicate: 'agent.process_flows.duplicate',
      delete: 'agent.process_flows.delete',
      toggleActive: 'agent.process_flows.toggle_active',
      viewExecutions: 'agent.process_flows.view_executions',
    },
    conversationalFlows: {
      view: 'agent.conversational_flows.view',
      create: 'agent.conversational_flows.create',
      edit: 'agent.conversational_flows.edit',
      duplicate: 'agent.conversational_flows.duplicate',
      delete: 'agent.conversational_flows.delete',
      toggleActive: 'agent.conversational_flows.toggle_active',
    },
    intents: {
      view: 'agent.intents.view',
    },
    escalations: {
      view: 'agent.escalations.view',
      create: 'agent.escalations.create',
      edit: 'agent.escalations.edit',
      delete: 'agent.escalations.delete',
    },
    channels: {
      view: 'agent.channels.view',
      configure: 'agent.channels.configure',
      toggleActive: 'agent.channels.toggle_active',
    },
    metrics: {
      view: 'agent.metrics.view',
    },
    evals: {
      view: 'agent.evals.view',
      create: 'agent.evals.create',
      run: 'agent.evals.run',
      viewResults: 'agent.evals.view_results',
    },
    settings: {
      view: 'agent.settings.view',
      editPersonality: 'agent.settings.edit_personality',
      editBehavior: 'agent.settings.edit_behavior',
      editCapabilities: 'agent.settings.edit_capabilities',
    },
    billing: {
      view: 'agent.billing.view',
      edit: 'agent.billing.edit',
    },
  },
  table: {
    view: 'table.view',
    create: 'table.create',
    rename: 'table.rename',
    move: 'table.move',
    delete: 'table.delete',
    shareAccess: 'table.share_access',
    connectAgents: 'table.connect_agents',
    column: {
      add: 'table.column.add',
      edit: 'table.column.edit',
      delete: 'table.column.delete',
    },
    addRow: 'table.add_row',
    editRow: 'table.edit_row',
    deleteRow: 'table.delete_row',
    filter: 'table.filter',
    export: 'table.export',
  },
  /** The agent activity log (PRD - Observability, Feature 4). Does not inherit `agent.view`. */
  observability: {
    view: 'observability.view',
    viewDetail: 'observability.view_detail',
  },
  /** Silia Learning (CIB-1256): the in-app guide opens only for a role that holds it. */
  learning: {
    view: 'learning.view',
  },
} as const;

/**
 * Flat list of all 122 atomic permission strings. Used for validation and to
 * derive the PermissionString union type. Kept in the same order as the seed.
 */
export const ALL_PERMISSIONS = [
  'platform.accounts.create',
  'platform.accounts.view_assigned',
  'platform.accounts.switch',
  'platform.implementers.view',
  'platform.implementers.assign_account',
  'platform.implementers.revoke_account',
  'account.information.view',
  'account.information.edit',
  'account.billing.view',
  'account.billing.edit',
  'account.invoices.view',
  'account.invoices.download',
  'organization.users.view',
  'organization.users.create',
  'organization.users.edit',
  'organization.users.import',
  'organization.users.deactivate',
  'organization.users.reactivate',
  'organization.users.delete',
  'organization.users.access.view',
  'organization.users.access.manage',
  'organization.teams.view',
  'organization.teams.view_own',
  'organization.teams.create',
  'organization.teams.edit',
  'organization.teams.delete',
  'organization.teams.manage_members',
  'organization.teams.access.view',
  'organization.teams.access.manage',
  'organization.roles.view',
  'organization.roles.create',
  'organization.roles.edit',
  'organization.roles.delete',
  'organization.roles.clone',
  'organization.roles.assign',
  'folder.view',
  'folder.create',
  'folder.rename',
  'folder.move',
  'folder.delete',
  'folder.share_access',
  'agent.view',
  'agent.create',
  'agent.rename',
  'agent.move',
  'agent.delete',
  'agent.share_access',
  'agent.connect_tables',
  'agent.connect_qa',
  'agent.inbox.view',
  'agent.inbox.take_control',
  'agent.inbox.send_reply',
  'agent.inbox.resolve_ticket',
  'agent.inbox.reassign',
  'agent.inbox.force_escalate',
  'agent.qa.overview.view',
  'agent.qa.upload.view',
  'agent.qa.inbox.view',
  'agent.qa.sampling.view',
  'agent.qa.evaluations.view',
  'agent.qa.scorecards.view',
  'agent.qa.key_moments.view',
  'agent.qa.data_views.view',
  'agent.qa.analytics.view',
  // Faltaba: entró al catálogo anidado con la card de Training y nunca acá, así que no
  // era un `PermissionString` válido y el test de paridad venía fallando en develop.
  'agent.qa.training.view',
  'agent.qa.knowledge_base.view',
  'agent.qa.settings.view',
  'agent.kb.view',
  'agent.kb.upload_source',
  'agent.kb.sync_website',
  'agent.kb.edit_source',
  'agent.kb.delete_source',
  'agent.process_flows.view',
  'agent.process_flows.create',
  'agent.process_flows.edit',
  'agent.process_flows.duplicate',
  'agent.process_flows.delete',
  'agent.process_flows.toggle_active',
  'agent.process_flows.view_executions',
  'agent.conversational_flows.view',
  'agent.conversational_flows.create',
  'agent.conversational_flows.edit',
  'agent.conversational_flows.duplicate',
  'agent.conversational_flows.delete',
  'agent.conversational_flows.toggle_active',
  'agent.intents.view',
  'agent.escalations.view',
  'agent.escalations.create',
  'agent.escalations.edit',
  'agent.escalations.delete',
  'agent.channels.view',
  'agent.channels.configure',
  'agent.channels.toggle_active',
  'agent.metrics.view',
  'agent.evals.view',
  'agent.evals.create',
  'agent.evals.run',
  'agent.evals.view_results',
  'agent.settings.view',
  'agent.settings.edit_personality',
  'agent.settings.edit_behavior',
  'agent.settings.edit_capabilities',
  'agent.billing.view',
  'agent.billing.edit',
  'table.view',
  'table.create',
  'table.rename',
  'table.move',
  'table.delete',
  'table.share_access',
  'table.connect_agents',
  'table.column.add',
  'table.column.edit',
  'table.column.delete',
  'table.add_row',
  'table.edit_row',
  'table.delete_row',
  'table.filter',
  'table.export',
  'observability.view',
  'observability.view_detail',
  'learning.view',
] as const;

/** Union of all valid atomic permission strings. */
export type PermissionString = (typeof ALL_PERMISSIONS)[number];
